import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getHeaders } from 'auth/auth';
import { api as loanServiceAPI } from 'constants/constant';
import { documentAPI } from './document';
import { pipelineAPI } from './pipeline';

const _METADATA_SECTION_KEYS = [
  'personal_details',
  'employment_and_income',
  'user_asset_details',
  'user_liabilities_details',
  'user_reos_details',
  'application_declaration',
  'demographics_details',
];

// Sections whose backend save runs evaluate_and_cache. After one of these
// fires, the cached B2C documents list is stale — invalidate it so the
// Documents tab refetches without a full page reload.
const _DOC_REC_TRIGGER_SECTIONS = new Set([
  'personal_details',
  'employment_and_income',
  'reos',
  'assets',
  'loan_details',
]);

export const applicationAPI = createApi({
  reducerPath: 'profile',
  baseQuery: fetchBaseQuery({
    baseUrl: loanServiceAPI,
    credentials: 'include',
  }),
  tagTypes: ['BorrowerProfile', 'ProfileBootstrap', 'CoBorrowers', 'BorrowerScopedProfile'],
  endpoints: (builder) => ({
    getBorrowerProfile: builder.query({
      query: ({ applicationId, mode }) => {
        return {
          url: `b2c/loan/dashboard/borrower_profile/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
          params: mode ? { mode } : undefined,
        };
      },
      providesTags: ['BorrowerProfile'],
      keepUnusedDataFor: 300,
    }),
    getProfileBootstrap: builder.query({
      query: ({ applicationId }) => {
        return {
          url: `b2c/composite/profile/init/${applicationId}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['ProfileBootstrap'],
      keepUnusedDataFor: 300,
    }),
    createPersonalDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/personal_details`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updatePersonalDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/personal_details/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createLoanDetails: builder.mutation({
      query: (data) => {
        const body =
          data && typeof data === 'object' && 'data' in data
            ? {
                ...(data.data || {}),
                applicationId:
                  data.applicationId ??
                  data.data?.applicationId,
              }
            : data;
        return {
          url: `b2c/loan/dashboard/loan`,
          method: 'POST',
          body,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updateLoanDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/loan/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createEmploymentDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updateEmploymentDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createAssetDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/assets`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updateAssetDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/assets`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    deleteAssetDetails: builder.mutation({
      query: ({ assetId, userId, applicationId, borrowerId }) => {
        const params = {
          ...(userId ? { user_id: userId } : {}),
          ...(applicationId ? { application_id: applicationId } : {}),
          ...(borrowerId ? { borrower_id: borrowerId } : {}),
        };
        return {
          url: `b2c/loan/dashboard/assets/${assetId}`,
          method: 'DELETE',
          params,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createLiabilitiesDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updateLiabilitiesDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createRealEstateDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updateRealEstateDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createDeclarationDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/declaration`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updateDeclarationDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/declaration/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createDemographicsDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/dashboard/demographics`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    updateDemographicsDetails: builder.mutation({
      query: ({ applicationId, data }) => {
        return {
          url: `b2c/loan/dashboard/demographics/${applicationId}`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),

    getOwnerShip: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/ownership`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getLoanPurpose: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/loan_purpose`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getLoanType: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/loan_type`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getOccupancy: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/occupancy`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getLoanProgram: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/loan_program`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getEmploymentType: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/employment_type`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getBusinessType: builder.query({
      query: () => ({
        url: `/b2c/loan/application_dropdowns/business_type`,
        method: 'GET',
        headers: getHeaders(),
      }),
      keepUnusedDataFor: 300,
    }),

    getMilitaryBranch: builder.query({
      query: () => ({
        url: `/b2c/loan/application_dropdowns/military_branch`,
        method: 'GET',
        headers: getHeaders(),
      }),
      keepUnusedDataFor: 300,
    }),

    getMilitaryDischargeStatus: builder.query({
      query: () => ({
        url: `/b2c/loan/application_dropdowns/military_discharge_status`,
        method: 'GET',
        headers: getHeaders(),
      }),
      keepUnusedDataFor: 300,
    }),

    getPropertyType: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/property_type`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getAccountType: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/account_type`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getLiabilityType: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/liability_type`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getRefinancePurpose: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/refinance_purpose`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getMartialStatus: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/marital_status`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getCitizenshipStatus: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/citizenship_status`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getUserRace: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/race`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getAssetType: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/asset_type`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getPropertyRight: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/property_right`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getMannerHeld: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/manner_held`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getLoanTermYears: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/loan_term`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getIncomeType: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/income_type`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getApplicationStatus: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/application_status`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),

    getEthnicity: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/ethnicity`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),
    // get state
    getStates: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/states`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),
    getCountiesByState: builder.query({
      query: (stateCode) => {
        return {
          url: `/b2c/loan/application_dropdowns/counties/${stateCode}`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      keepUnusedDataFor: 300,
    }),
    inviteCoBorrower: builder.mutation({
      query: ({ applicationId, firstName, lastName, email, phoneNumber, relationship }) => {
        return {
          url: `b2c/loan/co_borrower/invite`,
          method: 'POST',
          body: {
            applicationId,
            firstName,
            lastName,
            email,
            phoneNumber,
            relationship,
          },
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile', 'CoBorrowers'],
    }),
    //create new dynamic employee
    createDynamicEmploymentDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    //update exsiting dynamic employee
    updateDynamicEmploymentDetails: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/employment_and_income`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    //delete exsiting dynamic employee
    deleteDynamicEmploymentDetails: builder.mutation({
      query: ({ employmentIncomeId, userId }) => {
        const params = userId ? { user_id: userId } : undefined;
        return {
          url: `b2c/loan/dashboard/employment_and_income/${employmentIncomeId}`,
          method: 'DELETE',
          params,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    //create new dynamic liability
    createDynamicLiability: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    //update exsiting dynamic liability
    updateDynamicLiability: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/liabilities`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    //delete exsiting dynamic liability
    deleteDynamicLiability: builder.mutation({
      query: ({ userLiabilityId, userId, applicationId, borrowerId }) => {
        const params = {
          ...(userId ? { user_id: userId } : {}),
          ...(applicationId ? { application_id: applicationId } : {}),
          ...(borrowerId ? { borrower_id: borrowerId } : {}),
        };
        return {
          url: `b2c/loan/dashboard/liabilities/${userLiabilityId}`,
          method: 'DELETE',
          params,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    createRealEstateDetailsV1: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),

    deleteRealEstateById: builder.mutation({
      query: ({ reosId, userId }) => {
        const params = userId ? { user_id: userId } : undefined;
        return {
          url: `b2c/loan/dashboard/reos/${reosId}`,
          method: 'DELETE',
          params,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),

    autoSyncPrimaryReo: builder.mutation({
      query: (payload) => ({
        url: `b2c/loan/loa/reos/auto-sync-primary`,
        method: 'POST',
        body: payload,
        headers: getHeaders(),
      }),
      invalidatesTags: ['BorrowerProfile', 'BorrowerScopedProfile'],
    }),

    updateRealEstateDetailsV1: builder.mutation({
      query: ({ data }) => {
        return {
          url: `b2c/loan/dashboard/reos`,
          method: 'PUT',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    getDocumentCategory: builder.query({
      query: () => {
        return {
          url: `/b2c/loan/application_dropdowns/document_category`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
    }),
    // Spouse / co-borrower personal details (B2C)
    createSpousePersonalDetails: builder.mutation({
      query: (data) => {
        return {
          url: `b2c/loan/co_borrower/personal_details`,
          method: 'POST',
          body: data,
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile'],
    }),
    // Get all co-borrowers for a loan application (returns only accepted co-borrowers)
    getLoanCoborrowers: builder.query({
      query: ({ loanId }) => {
        return {
          url: `b2c/loan/${loanId}/co_borrowers`,
          method: 'GET',
          headers: getHeaders(),
        };
      },
      providesTags: ['CoBorrowers'],
      keepUnusedDataFor: 300,
    }),
    // Remove a co-borrower from a loan application
    removeCoborrower: builder.mutation({
      query: ({ coborrower_id, loan_id }) => {
        return {
          url: `b2c/loan/co_borrower/${coborrower_id}`,
          method: 'DELETE',
          headers: getHeaders(),
        };
      },
      invalidatesTags: ['BorrowerProfile', 'CoBorrowers'],
    }),

    // ─── Borrower-scoped profile endpoints ───
    // applicationId scopes the response to a specific loan application.
    // The cache key must include it so switching applications in the FE
    // dropdown does not re-use another application's cached payload.
    getBorrowerScopedProfile: builder.query({
      query: ({ borrowerId, section, applicationId }) => ({
        url: `b2c/loan/borrower/${borrowerId}/${section}`,
        method: 'GET',
        params: applicationId ? { applicationId } : undefined,
        headers: getHeaders(),
      }),
      providesTags: (result, error, { borrowerId, section, applicationId }) => [
        { type: 'BorrowerScopedProfile', id: `${borrowerId}-${section}-${applicationId || 'mru'}` },
      ],
      keepUnusedDataFor: 300,
    }),

    // ─── Bulk: all sections in one request ───
    getBorrowerAllSections: builder.query({
      query: ({ borrowerId, applicationId }) => ({
        url: `b2c/loan/borrower/${borrowerId}/all-sections`,
        method: 'GET',
        params: applicationId ? { applicationId } : undefined,
        headers: getHeaders(),
      }),
      providesTags: (result, error, { borrowerId, applicationId }) =>
        _METADATA_SECTION_KEYS.map((s) => ({
          type: 'BorrowerScopedProfile',
          id: `${borrowerId}-${s}-${applicationId || 'mru'}`,
        })),
      keepUnusedDataFor: 300,
    }),

    updateBorrowerScopedProfile: builder.mutation({
      query: ({ borrowerId, section, data, applicationId }) => ({
        url: `b2c/loan/borrower/${borrowerId}/${section}`,
        method: 'PUT',
        body: data,
        params: applicationId ? { applicationId } : undefined,
        headers: getHeaders(),
      }),
      // Use the refreshed data from the PUT response to update the cache
      // immediately — avoids a separate GET round-trip after every save.
      async onQueryStarted({ borrowerId, section, applicationId }, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          if (result?.refreshed) {
            dispatch(
              applicationAPI.util.upsertQueryData(
                'getBorrowerScopedProfile',
                { borrowerId, section, applicationId },
                { success: true, data: result.refreshed }
              )
            );
          }
          // Backend ran evaluate_and_cache for this section — invalidate the
          // documents list in the documentAPI slice so the Documents tab
          // pulls fresh requirements on next view (no page reload needed).
          if (_DOC_REC_TRIGGER_SECTIONS.has(section)) {
            dispatch(documentAPI.util.invalidateTags(['updateList']));
          }
          // Loan details changes (loan_purpose, loan_amount, property
          // address, etc.) are surfaced on the My Applications list,
          // which lives in a separate RTK Query slice. Tag invalidation
          // doesn't cross slices, so we have to poke pipelineAPI
          // explicitly — otherwise the user has to hard-reload the
          // pipeline page to see the change.
          if (section === 'loan_details') {
            dispatch(pipelineAPI.util.invalidateTags(['updatePipeline']));
          }
        } catch {
          // If the PUT failed, invalidation will handle it
        }
      },
      invalidatesTags: (result, error, { borrowerId, section, applicationId }) => {
        const tags = [
          { type: 'BorrowerScopedProfile', id: `${borrowerId}-${section}-${applicationId || 'mru'}` },
          // Always invalidate BorrowerProfile so the summary query re-fetches.
          // Loan Info reads from the summary endpoint, not the scoped endpoint.
          'BorrowerProfile',
        ];
        return tags;
      },
    }),

    // ─── Add spouse (no invitation) ───
    addSpouseCoBorrower: builder.mutation({
      query: (data) => ({
        url: `b2c/loan/co_borrower/add-spouse`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      invalidatesTags: ['CoBorrowers', 'ProfileBootstrap'],
    }),
  }),
});

export const {
  useGetProfileBootstrapQuery,
  useGetBorrowerProfileQuery,
  useCreatePersonalDetailsMutation,
  useUpdatePersonalDetailsMutation,
  useCreateLoanDetailsMutation,
  useUpdateLoanDetailsMutation,
  useCreateEmploymentDetailsMutation,
  useUpdateEmploymentDetailsMutation,
  useCreateAssetDetailsMutation,
  useUpdateAssetDetailsMutation,
  useDeleteAssetDetailsMutation,
  useCreateLiabilitiesDetailsMutation,
  useUpdateLiabilitiesDetailsMutation,
  useCreateRealEstateDetailsMutation,
  useUpdateRealEstateDetailsMutation,
  useCreateDeclarationDetailsMutation,
  useUpdateDeclarationDetailsMutation,
  useCreateDemographicsDetailsMutation,
  useUpdateDemographicsDetailsMutation,
  useGetOwnerShipQuery,
  useGetLoanPurposeQuery,
  useGetLoanTypeQuery,
  useGetOccupancyQuery,
  useGetLoanProgramQuery,
  useGetPropertyTypeQuery,
  useGetAccountTypeQuery,
  useGetLiabilityTypeQuery,
  useGetRefinancePurposeQuery,
  useGetEthnicityQuery,
  useGetUserRaceQuery,
  useGetEmploymentTypeQuery,
  useGetBusinessTypeQuery,
  useGetMilitaryBranchQuery,
  useGetMilitaryDischargeStatusQuery,
  useGetMartialStatusQuery,
  useGetCitizenshipStatusQuery,
  useGetAssetTypeQuery,
  useGetLoanTermYearsQuery,
  useGetIncomeTypeQuery,
  useGetMannerHeldQuery,
  useGetPropertyRightQuery,
  useGetApplicationStatusQuery,
  useGetStatesQuery,
  useGetCountiesByStateQuery,
  useUpdateRealEstateDetailsV1Mutation,
  useCreateRealEstateDetailsV1Mutation,
  useDeleteRealEstateByIdMutation,
  useAutoSyncPrimaryReoMutation,
  useUpdateDynamicEmploymentDetailsMutation,
  useCreateDynamicEmploymentDetailsMutation,
  useDeleteDynamicEmploymentDetailsMutation,
  useCreateDynamicLiabilityMutation,
  useUpdateDynamicLiabilityMutation,
  useDeleteDynamicLiabilityMutation,
  useGetDocumentCategoryQuery,
  useInviteCoBorrowerMutation,
  useCreateSpousePersonalDetailsMutation,
  useGetLoanCoborrowersQuery,
  useRemoveCoborrowerMutation,
  useGetBorrowerScopedProfileQuery,
  useGetBorrowerAllSectionsQuery,
  useUpdateBorrowerScopedProfileMutation,
  useAddSpouseCoBorrowerMutation,
} = applicationAPI;
